import { useModal } from 'contexts/ModalContext'
import { Field, Form, Formik } from 'formik'
import PropTypes from 'prop-types'
import * as Yup from 'yup'

const normalizeInitialValues = (fields = {}) => {
  const out = { ...(fields || {}) }
  for (const k of [
    'codigo_franquicia',
    'codigo_fq_3dig',
    'nombre',
    'propiedad_franquicia',
    'cif_nif',
    'iban',
    'email',
    'columna1',
    'columna2'
  ]) {
    if (out[k] === null || out[k] === undefined) out[k] = ''
  }
  return out
}

export default function FranquiciasFormDetail({ isNew, user, fields, franquiciaRef, handleSubmit, handleDelete }) {
  const { openModal } = useModal()
  const isAdmin = Number(user?.role_id) === 1 || Number(user?.role_id) === 2
  const canDelete = Number(user?.role_id) === 1

  // SOLO obligatorio codigo_franquicia (tal como pides). El resto opcional.
  const validationSchema = Yup.object().shape({
    codigo_franquicia: Yup.string().required('Campo obligatorio'),
    codigo_fq_3dig: Yup.string().nullable(),
    nombre: Yup.string().nullable(),
    propiedad_franquicia: Yup.string().nullable(),
    cif_nif: Yup.string().nullable(),
    iban: Yup.string().nullable(),
    email: Yup.string().email('Email no válido').nullable(),
    columna1: Yup.string().nullable(),
    columna2: Yup.string().nullable()
  })

  return (
    <Formik
      enableReinitialize
      initialValues={normalizeInitialValues(fields)}
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        await handleSubmit(values)
      }}
    >
      {({ values, errors, touched }) => (
        <Form
          ref={franquiciaRef}
          className="flex flex-col w-full p-6 mx-auto mt-6 bg-white border border-gray-200 rounded-md dark:border-gray-700 dark:bg-gray-800 gap-y-4"
        >
          <div className="grid grid-cols-12 gap-4">
            {/* codigo_franquicia */}
            <div className="col-span-12 md:col-span-3">
              <label
                className={`${
                  touched.codigo_franquicia && errors.codigo_franquicia
                    ? 'text-red-700 dark:text-red-500'
                    : 'text-gray-700 dark:text-gray-200'
                }`}
                htmlFor="codigo_franquicia"
              >
                Código franquicia
              </label>
              <Field
                id="codigo_franquicia"
                name="codigo_franquicia"
                type="text"
                className={`block w-full p-2 mt-2 border bg-white rounded-sm focus:ring-opacity-40 focus:outline-none focus:ring disabled:opacity-50 ${
                  touched.codigo_franquicia && errors.codigo_franquicia
                    ? 'border-red-500 text-red-900 focus:ring-red-500'
                    : 'text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-blue-400 focus:ring-blue-300'
                }`}
                disabled={!isAdmin || !isNew}
              />
              {touched.codigo_franquicia && errors.codigo_franquicia ? (
                <p className="mt-1 text-sm text-red-600">{errors.codigo_franquicia}</p>
              ) : null}
            </div>

            {/* codigo_fq_3dig */}
            <div className="col-span-12 md:col-span-3">
              <label className="text-gray-700 dark:text-gray-200" htmlFor="codigo_fq_3dig">
                Código 3 dígitos
              </label>
              <Field
                id="codigo_fq_3dig"
                name="codigo_fq_3dig"
                type="text"
                className="block w-full p-2 mt-2 border bg-white rounded-sm focus:ring-opacity-40 focus:outline-none focus:ring text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-blue-400 focus:ring-blue-300"
                disabled={!isAdmin}
              />
            </div>

            {/* cif_nif */}
            <div className="col-span-12 md:col-span-3">
              <label className="text-gray-700 dark:text-gray-200" htmlFor="cif_nif">
                CIF/NIF
              </label>
              <Field
                id="cif_nif"
                name="cif_nif"
                type="text"
                className="block w-full p-2 mt-2 border bg-white rounded-sm focus:ring-opacity-40 focus:outline-none focus:ring text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-blue-400 focus:ring-blue-300"
                disabled={!isAdmin}
              />
            </div>

            {/* nombre */}
            <div className="col-span-12 md:col-span-6">
              <label
                className={`${
                  touched.nombre && errors.nombre ? 'text-red-700 dark:text-red-500' : 'text-gray-700 dark:text-gray-200'
                }`}
                htmlFor="nombre"
              >
                Nombre
              </label>
              <Field
                id="nombre"
                name="nombre"
                type="text"
                className={`block w-full p-2 mt-2 border bg-white rounded-sm focus:ring-opacity-40 focus:outline-none focus:ring disabled:opacity-50 ${
                  touched.nombre && errors.nombre
                    ? 'border-red-500 text-red-900 focus:ring-red-500'
                    : 'text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-blue-400 focus:ring-blue-300'
                }`}
                disabled={!isAdmin}
              />
              {touched.nombre && errors.nombre ? <p className="mt-1 text-sm text-red-600">{errors.nombre}</p> : null}
            </div>

            {/* propiedad_franquicia */}
            <div className="col-span-12 md:col-span-6">
              <label className="text-gray-700 dark:text-gray-200" htmlFor="propiedad_franquicia">
                Propiedad franquicia
              </label>
              <Field
                id="propiedad_franquicia"
                name="propiedad_franquicia"
                type="text"
                className="block w-full p-2 mt-2 border bg-white rounded-sm focus:ring-opacity-40 focus:outline-none focus:ring text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-blue-400 focus:ring-blue-300"
                disabled={!isAdmin}
              />
            </div>

            {/* iban */}
            <div className="col-span-12 md:col-span-6">
              <label className="text-gray-700 dark:text-gray-200" htmlFor="iban">
                IBAN
              </label>
              <Field
                id="iban"
                name="iban"
                type="text"
                className="block w-full p-2 mt-2 border bg-white rounded-sm focus:ring-opacity-40 focus:outline-none focus:ring text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-blue-400 focus:ring-blue-300"
                disabled={!isAdmin}
              />
            </div>

            {/* email */}
            <div className="col-span-12 md:col-span-6">
              <label
                className={`${
                  touched.email && errors.email
                    ? 'text-red-700 dark:text-red-500'
                    : 'text-gray-700 dark:text-gray-200'
                }`}
                htmlFor="email"
              >
                Email
              </label>
              <Field
                id="email"
                name="email"
                type="email"
                className={`block w-full p-2 mt-2 border bg-white rounded-sm focus:ring-opacity-40 focus:outline-none focus:ring disabled:opacity-50 ${
                  touched.email && errors.email
                    ? 'border-red-500 text-red-900 focus:ring-red-500'
                    : 'text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-blue-400 focus:ring-blue-300'
                }`}
                disabled={!isAdmin}
              />
              {touched.email && errors.email ? (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              ) : null}
            </div>

            {/* columna1 */}
            <div className="col-span-12 md:col-span-3">
              <label className="text-gray-700 dark:text-gray-200" htmlFor="columna1">
                Columna 1
              </label>
              <Field
                id="columna1"
                name="columna1"
                type="text"
                className="block w-full p-2 mt-2 border bg-white rounded-sm focus:ring-opacity-40 focus:outline-none focus:ring text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-blue-400 focus:ring-blue-300"
                disabled={!isAdmin}
              />
            </div>

            {/* columna2 */}
            <div className="col-span-12 md:col-span-3">
              <label className="text-gray-700 dark:text-gray-200" htmlFor="columna2">
                Columna 2
              </label>
              <Field
                id="columna2"
                name="columna2"
                type="text"
                className="block w-full p-2 mt-2 border bg-white rounded-sm focus:ring-opacity-40 focus:outline-none focus:ring text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-blue-400 focus:ring-blue-300"
                disabled={!isAdmin}
              />
            </div>
          </div>

          {/* Footer botones */}
          {isAdmin ? (
            <div className="flex flex-col gap-3 pt-6 mt-2 border-t border-gray-200 md:flex-row md:items-center md:justify-end dark:border-gray-700">
              {!isNew && canDelete ? (
                <button
                  type="button"
                  onClick={() =>
                    openModal('DeleteModal', {
                      itemName: values?.nombre || values?.codigo_franquicia,
                      itemId: values?.codigo_franquicia,
                      handleDelete
                    })
                  }
                  className="w-1/2 px-5 py-2 text-sm text-white transition-colors duration-200 bg-red-500 border border-red-500 rounded-sm sm:w-36 dark:hover:bg-gray-800 enabled:hover:bg-white enabled:hover:text-red-500 dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:enabled:hover:bg-gray-800 disabled:opacity-30 focus:ring-opacity-40 focus:outline-none focus:ring focus:ring-red-500 focus:border-red-500 dark:focus:border-red-500"
                >
                  Eliminar
                </button>
              ) : null}

              <button
                type="submit"
                className="w-1/2 px-5 py-2 text-sm text-white transition-colors duration-200 bg-[#FCAE52] border border-[#FCAE52] rounded-sm sm:w-36 dark:hover:bg-gray-800 enabled:hover:bg-white enabled:hover:text-[#FCAE52] dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:enabled:hover:bg-gray-800 disabled:opacity-30 focus:ring-opacity-40 focus:outline-none focus:ring focus:ring-[#FCAE52] focus:border-[#FCAE52] dark:focus:border-[#FCAE52]"
              >
                Guardar
              </button>
            </div>
          ) : null}
        </Form>
      )}
    </Formik>
  )
}

FranquiciasFormDetail.propTypes = {
  isNew: PropTypes.bool,
  user: PropTypes.object,
  fields: PropTypes.object,
  franquiciaRef: PropTypes.any,
  handleSubmit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired
}
